import { Arrays, Cache, Terminable, Terminator, UUID } from "@opendaw/lib-std";
import { Propagation } from "@opendaw/lib-box";
import { Pointers } from "@opendaw/studio-enums";
import { ValueEventBox } from "@opendaw/studio-boxes";
import { ValueEventCollectionBoxAdapter } from "../collection/ValueEventCollectionBoxAdapter";
import { InterpolationFieldAdapter } from "./InterpolationFieldAdapter";
export class ValueEventBoxAdapter {
    static Comparator = (a, b) => {
        const positionDiff = a.position - b.position;
        if (positionDiff !== 0) {
            return positionDiff;
        }
        const indexDiff = a.index - b.index;
        if (indexDiff !== 0) {
            return indexDiff;
        }
        throw new Error(`${a} and ${b} are identical in terms of comparison`);
    };
    type = "value-event";
    #terminator = new Terminator();
    #context;
    #box;
    #interpolation;
    #interpolationSubscription;
    #isSelected = false;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#interpolation = this.#terminator.own(new Cache(() => InterpolationFieldAdapter.read(this.#box.interpolation)));
        const invalidateInterpolation = () => {
            this.#interpolation.invalidate();
            this.collection.ifSome(collection => collection.onEventPropertyChanged());
        };
        this.#terminator.ownAll(this.#box.subscribe(Propagation.Children, (update) => {
            if (this.collection.isEmpty()) {
                return;
            }
            if (update.type === "primitive" || update.type === "pointer") {
                const collection = this.collection.unwrap();
                const updatedFieldKeys = update.address.fieldKeys;
                const indexChanged = Arrays.equals(this.#box.index.address.fieldKeys, updatedFieldKeys);
                const positionChanged = Arrays.equals(this.#box.position.address.fieldKeys, updatedFieldKeys);
                if (indexChanged || positionChanged) {
                    collection.requestSorting();
                }
                else {
                    collection.onEventPropertyChanged();
                }
            }
        }), this.#box.interpolation.subscribe(invalidateInterpolation), this.#box.interpolation.pointerHub.subscribe({
            onAdded: ({ box }) => {
                this.#interpolationSubscription.terminate();
                this.#interpolationSubscription = box.subscribe(Propagation.Children, invalidateInterpolation);
                invalidateInterpolation();
            },
            onRemoved: () => {
                this.#interpolationSubscription.terminate();
                this.#interpolationSubscription = Terminable.Empty;
                invalidateInterpolation();
            }
        }));
        this.#interpolationSubscription = this.#box.interpolation.pointerHub
            .filter(Pointers.ValueInterpolation)
            .at(0)?.box
            .subscribe(Propagation.Children, invalidateInterpolation)
            ?? Terminable.Empty;
    }
    onSelected() {
        this.#isSelected = true;
        this.collection.ifSome(region => region.onEventPropertyChanged());
    }
    onDeselected() {
        this.#isSelected = false;
        this.collection.ifSome(region => region.onEventPropertyChanged());
    }
    terminate() {
        this.#interpolationSubscription.terminate();
        this.#terminator.terminate();
    }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get position() { return this.#box.position.getValue(); }
    get index() { return this.#box.index.getValue(); }
    set interpolation(value) { InterpolationFieldAdapter.write(this.#box.interpolation, value); }
    get interpolation() { return this.#interpolation.get(); }
    get value() { return this.#box.value.getValue(); }
    get isSelected() { return this.#isSelected; }
    get collection() {
        return this.#box.events.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, ValueEventCollectionBoxAdapter));
    }
    copyTo(options) {
        const eventBox = ValueEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(options?.position ?? this.position);
            box.index.setValue(options?.index ?? this.index);
            box.events.refer(options?.events ?? this.collection.unwrap().box.events);
            box.value.setValue(options?.value ?? this.value);
        });
        InterpolationFieldAdapter.write(eventBox.interpolation, options?.interpolation ?? this.interpolation);
        return this.#context.boxAdapters.adapterFor(eventBox, ValueEventBoxAdapter);
    }
    copyFrom(options) {
        this.#box.position.setValue(options?.position ?? this.position);
        this.#box.index.setValue(options?.index ?? this.index);
        this.#box.events.refer(options?.events ?? this.collection.unwrap().box.events);
        this.#box.value.setValue(options?.value ?? this.value);
        InterpolationFieldAdapter.write(this.#box.interpolation, options?.interpolation ?? this.interpolation);
        return this;
    }
    toString() { return `{ValueEventBoxAdapter position: ${this.position} index: ${this.index}}`; }
}
